import { useCallback, useMemo, useState } from 'react';
import { ColumnsPreferenceRecognizedLists } from '../../../../../services/modules/userContextPreferences/userContextPreferences.types';
import useTableColumns from '../../../hooks/useTableColumns';
import {
  BaseItem,
  Column,
  GetRowActions,
  InternalData,
  MenuType,
  RowAction,
} from '../VirtualizedTable.types';
import useTreeStructure from '../tree/hooks/useTreeStructure';
import { getParentCheckboxState, isParent } from '../virtualizedTable.utils';
import useCloseMenuOnScroll from './useCloseMenuOnScroll';
import { VIRTUALIZED_FAKE_ROW_ID } from './utils/useVirtualizedTable.consts';

export interface MenuStateParams {
  anchorEl: HTMLButtonElement | null;
  type: MenuType;
}

export interface UseVirtualizedTableParams<T> {
  columns: Column<T>[];
  disablePointerCursor?: boolean;
  externalData: T[];
  columnsPreferenceRecognizedList?: ColumnsPreferenceRecognizedLists;
  emptyState?: React.ReactNode;
  hasActiveFilters?: boolean;
  getRowActions?: GetRowActions<T>;
  onToggleSelectParent?: (parentItem: T, childItems: T[]) => void;
  isAllSelected?: boolean;
  isFirstLoading?: boolean;
  selectedRows?: string[];
}

const useVirtualizedTable = <T extends BaseItem>({
  columns,
  columnsPreferenceRecognizedList,
  disablePointerCursor,
  externalData,
  emptyState,
  hasActiveFilters,
  getRowActions,
  onToggleSelectParent,
  isAllSelected,
  isFirstLoading,
  selectedRows,
}: UseVirtualizedTableParams<T>) => {
  const [menu, setMenu] = useState<MenuStateParams>({
    anchorEl: null,
    type: MenuType.HIDE_COLUMNS,
  });
  const [actions, setActions] = useState<RowAction[]>([]);

  const handleClickAction = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, item: T) => {
      if (!getRowActions) {
        return;
      }

      setMenu({ anchorEl: event.currentTarget, type: MenuType.ITEM });
      setActions(getRowActions({ item }));
    },
    [getRowActions],
  );

  useCloseMenuOnScroll({
    anchorEl: menu.anchorEl,
    onClose: () => setMenu((prev) => ({ ...prev, anchorEl: null })),
  });

  const selectedRowsSet = new Set(selectedRows);

  const parentIdSet = new Set(
    externalData.filter((item, i) => isParent(item, externalData[i + 1])).map((item) => item.id),
  );

  const treeStructure = useTreeStructure(externalData);

  const formattedData: InternalData<T>[] = externalData.map((item, index) => {
    const { depth, trunkContinuations } = treeStructure.get(item.id) ?? {
      depth: 0,
      trunkContinuations: [],
    };

    return {
      ...item,
      isSelected: isAllSelected || selectedRowsSet.has(item.id),
      isParent: isParent(item, externalData[index + 1]),
      isChild: !!item.parentId && parentIdSet.has(item.parentId),
      depth,
      trunkContinuations,
      disablePointerCursor,
    };
  });

  const availableColumns = columns.filter((column) => !column.hide);

  const { columnsToShow, canRestoreToDefaultColumns } = useTableColumns(
    availableColumns,
    columnsPreferenceRecognizedList,
  );

  const loadingData = useMemo(
    () =>
      [...Array(10).keys()].map((_, index) => ({
        id: `${index}-${VIRTUALIZED_FAKE_ROW_ID}`,
      })) as InternalData<T>[],
    [],
  );

  const data: InternalData<T>[] = isFirstLoading ? loadingData : formattedData;

  const isEmpty = !isFirstLoading && externalData.length === 0;
  const emptyStateToRender = isEmpty && !hasActiveFilters ? emptyState : undefined;

  const itemById = new Map(data.map((item) => [item.id, item]));
  const childItemsByParentId = new Map<string, InternalData<T>[]>();
  data.forEach((item) => {
    if (!item.parentId) {
      return;
    }

    let currentId: string | undefined = item.parentId;
    const visited = new Set<string>();

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const existing = childItemsByParentId.get(currentId) ?? [];
      existing.push(item);
      childItemsByParentId.set(currentId, existing);
      currentId = itemById.get(currentId)?.parentId;
    }
  });

  const dataWithParentSelection = data.map((item) => {
    if (!onToggleSelectParent || !item.isParent) {
      return item;
    }

    const childItems = childItemsByParentId.get(item.id) ?? [];
    const selectedChildCount = childItems.filter((child) => child.isSelected).length;
    const parentCheckboxState = getParentCheckboxState(selectedChildCount, childItems.length);

    return {
      ...item,
      isSelected: item.isSelected || parentCheckboxState === 'checked',
      parentCheckboxState,
    };
  });

  return {
    actions,
    availableColumns,
    canRestoreToDefaultColumns,
    childItemsByParentId,
    columnsToShow,
    data: dataWithParentSelection,
    emptyStateToRender,
    menu,
    onChangeMenu: (params: MenuStateParams) => setMenu(params),
    onClickAction: handleClickAction,
  };
};

export default useVirtualizedTable;
